<template>
  <div class="zone-edit">
    <div class="header">
      <h2>{{ isEdit ? '编辑' : '新增' }}</h2>
      <div>
        <a-button type="primary" @click="handleBack">
          <i class="iconfont icon-fanhuiicon mr5"></i> 返回
        </a-button>
      </div>
    </div>

    <div class="form-container">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          layout="vertical"
          class="edit-form"
      >
        <!-- 修改为水平布局的容器 -->
        <div class="horizontal-form-row">
          <!-- 左侧图片 -->
          <div class="form-left">
            <a-form-item label="" name="image">
              <image-uploader
                  v-model:image-url="formData.image"
                  :path-name="'testzone'"
                  :box-width="360"
                  :box-height="200"
              />
            </a-form-item>
          </div>

          <!-- 右侧信息 -->
          <div class="form-right">
            <!-- 为每个表单项单独设置布局 -->
            <div class="horizontal-form-row">
              <label class="form-label">名称：</label>
              <a-input
                  v-model:value="formData.name"
                  placeholder="请输入测试区域名称"
                  :maxlength="100"
                  show-count
                  disabled
                  class="horizontal-input"
              />
            </div>

            <div class="horizontal-form-row">
              <label class="form-label">测试区域类型：</label>
              <a-select
                  v-model:value="formData.type"
                  class="horizontal-input"
                  placeholder="请选择测试区域类型"
              >
                <a-select-option value="city">城市</a-select-option>
                <a-select-option value="highway">公路</a-select-option>
              </a-select>
            </div>

            <div class="horizontal-form-row">
              <label class="form-label">覆盖里程(km)：</label>
              <a-input-number
                  v-model:value="formData.mileage"
                  :min="0"
                  :step="0.1"
                  placeholder="请输入覆盖里程"
                  :maxlength="100"
                  show-count
                  class="horizontal-input"
              />
            </div>
          </div>

        </div>

        <a-form-item label="地址" name="address">
          <a-textarea
              v-model:value="formData.address"
              :auto-size="{ minRows: 2, maxRows: 2 }"
              class="large-textarea"
          />
        </a-form-item>

        <a-form-item label="区域描述" name="description">
          <a-textarea
              v-model:value="formData.description"
              :auto-size="{ minRows: 5, maxRows: 21 }"
              class="large-textarea"
          />
        </a-form-item>

        <a-form-item label="对接监管大屏编码" name="areaId">
          <a-input
              v-model:value="formData.areaId"
              placeholder="请输入对接监管大屏编码"
              :maxlength="100"
              show-count
              class="large-input"
          />
        </a-form-item>

        <div class="map-info">
          <div class="map-layout">
            <!-- 左侧输入区域 -->
            <div class="map-input-area">
              <!-- 绘图颜色编码 -->
              <div class="input-row">
                <label>绘图颜色编码：</label>
                <a-input
                    v-model:value="mapSettings.colorCode"
                    placeholder="请输入颜色编码，如 #FF0000"
                    class="color-input"
                />
              </div>
              <div class="input-row">
                <label>区域类型：</label>
                <a-select
                    v-model:value="mapSettings.type"
                    placeholder="请选择区域类型"
                    class="color-input"
                    @change="handleTypeChange"
                >
                  <a-select-option value="polygon">多边形</a-select-option>
                  <a-select-option value="line">线</a-select-option>
                  <a-select-option value="circle">圆</a-select-option>
                </a-select>
              </div>
              <!-- 区域信息（GeoJSON格式） -->
              <div class="input-row">
                <label>区域点位数据：</label>
                <a-textarea
                    v-model:value="mapSettings.geojsonData"
                    placeholder="请输入GeoJSON格式的区域数据"
                    :rows="8"
                    class="points-textarea"
                />
                <div class="help-text">格式：[[[经度,纬度],...]]</div>
              </div>

              <!-- 操作按钮 -->
              <div class="map-actions">
                <a-button type="primary" @click="drawPoints">绘制区域</a-button>
                <a-button @click="clearPoints">清除区域</a-button>
              </div>
            </div>

            <!-- 右侧地图显示区域 -->
            <div class="map-display-area">
              <div id="map-container" ref="mapContainer" class="map-container"></div>
            </div>
          </div>
        </div>

        <a-form-item class="form-actions">
          <a-button @click="handleBack">取消</a-button>
          <a-button class="ml10" type="primary" @click="handleSubmit">
            {{ isEdit ? '保存' : '新增' }}
          </a-button>
        </a-form-item>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { getZoneById, update } from '@/api/test/testPath';
import imageUploader from '@/components/imageUploader';
import AMapLoader from '@amap/amap-jsapi-loader';

const router = useRouter();
const route = useRoute();
const formRef = ref();

// 判断是否为编辑模式
const isEdit = computed(() => !!route.query.id);

// 表单数据
const formData = ref({
  id: undefined
});

// 表单验证规则
const formRules = {};

// 获取编辑数据
const getEditData = async () => {
  if (isEdit.value) {
    try {
      const res = await getZoneById(route.query.id);
      if (res.code === 200) {
        formData.value = res.data;

        // 如果有地图数据，加载到设置中
        if (formData.value.drawColor) {
          mapSettings.value.colorCode = formData.value.drawColor;
        }
        if (formData.value.category) {
          mapSettings.value.type = formData.value.category;
        }
        if (formData.value.coords) {
          mapSettings.value.geojsonData = JSON.stringify(formData.value.coords, null, 2);
        }
      }
    } catch (error) {
      message.error('获取测试区域信息失败');
      console.error('获取测试区域信息失败:', error);
    }
  }
};

const handleTypeChange = (value) => {
  mapSettings.value.type = value;
  // 同步到表单数据
  formData.value.category = value;
  // 如果有已绘制的图形，清除并重新绘制
  if (mapSettings.value.geojsonData) {
    try {
      const coordsData = JSON.parse(mapSettings.value.geojsonData);
      // 更新坐标数据的类型
      if (coordsData && typeof coordsData === 'object') {
        coordsData.type = value;
        mapSettings.value.geojsonData = JSON.stringify(coordsData, null, 2);
      }
    } catch (error) {
      console.error('更新坐标数据类型失败:', error);
    }
  }
};

// 提交表单
const handleSubmit = async () => {
  try {
    // 表单验证
    await formRef.value.validate();
    formData.value.id = route.query.id;
    if (formData.value.mileage !== undefined && formData.value.mileage !== null) {
      // 确保是数字类型
      formData.value.mileage = Number(formData.value.mileage);
    }
    formData.value.drawColor = mapSettings.value.colorCode;
    formData.value.category  = mapSettings.value.type;
    if (mapSettings.value.geojsonData) {
      formData.value.coords = JSON.parse(mapSettings.value.geojsonData);
    }
    if (isEdit.value) {
      const res = await update(formData.value)
      if (res.code === 200) {
        message.success('测试区域信息更新成功');
        await router.push('/zone/index');
      } else {
        message.error(res.message || '操作失败');
      }
    }
  } catch (error) {
    message.error('操作失败，请重试');
    console.error('提交表单出错:', error);
  }
};

// 地图设置数据
const mapSettings = ref({
  colorCode: '#1890ff', // 默认蓝色
  type: '',
  geojsonData: ''
});

// 地图实例和多边形引用
let mapInstance = null;
let drawnPolygons = ref([]);
let AMap = null;

// 初始化地图
const initMap = async () => {
  try {
    // 等待DOM更新完成
    await nextTick();

    const mapContainer = document.getElementById('map-container');
    if (!mapContainer) {
      console.error('地图容器未找到');
      return;
    }

    // 加载高德地图API
    AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be', // 使用与首页相同的API Key
      version: '2.0',
      Loca: {
        version: '2.0.0'
      },
      plugins: ['AMap.MouseTool', 'AMap.PolylineEditor', 'AMap.PolyEditor']
    });

    // 创建地图实例
    mapInstance = new AMap.Map('map-container', {
      zoom: 12,
      mapStyle: 'amap://styles/45311ae996a8bea0da10ad5151f72979', // 使用与首页相同的地图样式
      center: [103.726465, 36.107992] // 默认中心点（兰州）
    });

    mapInstance.setZooms([2, 30]);

    // 添加地图加载完成事件
    mapInstance.on('complete', () => {
      console.log('地图加载完成');
      // 如果有已保存的多边形数据，自动绘制
      if (mapSettings.value.geojsonData) {
        drawPoints();
      }
    });

    // 监听窗口大小变化，自动调整地图尺寸
    const handleResize = () => {
      if (mapInstance) {
        mapInstance.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    // 在组件销毁时移除事件监听
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

  } catch (error) {
    console.error('地图加载失败:', error);
    message.error('地图加载失败，请刷新页面重试');
  }
};

// 绘制几何图形（支持多边形、圆形和线）
const drawPoints = () => {
  try {
    // 清除已有图形
    clearPoints();

    // 解析坐标数据
    let coordinates = null;
    if (mapSettings.value.geojsonData.trim()) {
      try {
        coordinates = JSON.parse(mapSettings.value.geojsonData);
      } catch (parseError) {
        message.error('坐标数据格式错误，请检查');
        return;
      }
    }

    if (!coordinates || !Array.isArray(coordinates)) {
      message.warning('请输入有效的坐标数据');
      return;
    }

    // 获取几何类型
    const type = mapSettings.value.type?.toLowerCase();
    if (!type || !['polygon', 'line', 'circle'].includes(type)) {
      message.warning('请先选择区域类型：多边形、线或圆');
      return;
    }

    // 确保mapInstance已初始化
    if (!mapInstance || !AMap) {
      message.warning('地图正在加载中，请稍候再试');
      return;
    }

    let geometry = null;
    const color = mapSettings.value.colorCode || '#1E8EF2';

    // 根据类型绘制不同的几何图形
    switch (type) {
      case 'polygon':
        // 检查多边形坐标点数量
        if (coordinates.length < 3) {
          message.warning('多边形至少需要3个顶点');
          return;
        }

        // 创建多边形
        geometry = new AMap.Polygon({
          path: coordinates,
          zIndex: 50,
          bubble: true,
          strokeColor: color,
          strokeOpacity: 0.7,
          strokeWeight: 2,
          fillColor: adjustOpacity(color, 0.3),
          fillOpacity: 0.4,
          strokeStyle: 'solid'
        });
        break;

      case 'circle': {
        // 圆形需要至少2个点（圆心和半径参考点）
        if (coordinates.length < 2) {
          message.warning('圆形至少需要2个点：圆心和半径参考点');
          return;
        }

        const center = coordinates[0]; // 第一个点作为圆心
        // 计算到第二个点的距离作为半径（米）
        const radius = Math.sqrt(
            Math.pow(coordinates[1][0] - center[0], 2) +
            Math.pow(coordinates[1][1] - center[1], 2)
        ) * 111000; // 粗略转换为米

        if (radius <= 0) {
          message.warning('圆的半径必须大于0');
          return;
        }

        // 创建圆形
        geometry = new AMap.Circle({
          center: center,
          radius: radius,
          zIndex: 50,
          bubble: true,
          strokeColor: color,
          strokeOpacity: 0.7,
          strokeWeight: 2,
          fillColor: adjustOpacity(color, 0.3),
          fillOpacity: 0.4
        });
        break;
      }
      case 'line':
        // 检查线的坐标点数量
        if (coordinates.length < 2) {
          message.warning('线至少需要2个顶点');
          return;
        }

        // 创建线
        geometry = new AMap.Polyline({
          path: coordinates,
          zIndex: 50,
          bubble: true,
          strokeColor: color,
          strokeOpacity: 0.7,
          strokeWeight: 3,
          strokeStyle: 'solid',
          lineJoin: 'round',
          lineCap: 'round'
        });
        break;
    }

    // 添加到地图
    if (geometry) {
      mapInstance.add(geometry);
      drawnPolygons.value.push(geometry);

      // 调整地图视野，让图形完全可见
      mapInstance.setFitView([geometry]);

      // 构建提交数据
      const formDataCoords = {
        type: type,
        coordinates: coordinates
      };

      // 更新表单数据
      formData.value.drawColor = color;
      formData.value.coords = formDataCoords;
      formData.value.category = type; // 同步到category字段

      message.success(`${type === 'polygon' ? '多边形' : type === 'line' ? '线' : '圆'} 绘制完成`);
    }
  } catch (error) {
    console.error('绘制图形失败:', error);
    message.error('绘制失败，请检查数据格式');
  }
};
// 清除多边形
const clearPoints = () => {
  try {
    // 首先检查mapInstance是否存在
    if (!mapInstance) {
      console.warn('地图实例不存在');
      message.warning('地图尚未加载完成');
      return;
    }

    // 使用高德地图的清除覆盖物方法
    if (drawnPolygons.value.length > 0) {
      console.log('尝试批量移除多边形');
      mapInstance.remove(drawnPolygons.value);

      // 清空多边形数组
      drawnPolygons.value = [];
      console.log('多边形数组已清空');

      // 刷新地图视图
      mapInstance.clearMap();
      console.log('地图已清空所有覆盖物');

      message.success('区域已清除');
    }
  } catch (error) {
    console.error('清除区域失败:', error);
    message.error('清除区域失败，请重试');
  }
};

// 调整颜色透明度
const adjustOpacity = (hexColor, opacity) => {
  // 移除可能的#号
  hexColor = hexColor.replace('#', '');

  // 转换为RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // 返回带透明度的rgba颜色
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// 返回上一页
const handleBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/zone/index');
  }
};

// 页面加载时获取数据
onMounted(async () => {
  await getEditData();
  // 延迟加载地图，确保DOM已渲染
  setTimeout(() => {
    initMap();
  }, 100);
});

// 组件销毁时释放地图资源
onBeforeUnmount(() => {
  if (mapInstance) {
    mapInstance.destroy();
    mapInstance = null;
    console.log('地图已销毁');
  }
});
</script>

<style lang="less" scoped>
.zone-edit {
  padding: 20px;
  background-color: #ffffff;
  min-height: calc(100vh - 70px);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.form-container {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

.edit-form {
  max-width: 1000px;
  width: 100%;
  font-size: 14px;
}

/* 增大输入框 */
.large-input {
  font-size: 14px;
  line-height: 1.5;
  .ant-input {
    font-size: 14px;
    height: 40px;
  }
}

/* 增大文本域 */
.large-textarea {
  font-size: 14px;
  line-height: 1.6;
  .ant-input-textarea {
    font-size: 14px;
  }
}

/* 调整标签字体 */
.ant-form-item-label > label {
  font-size: 14px;
  font-weight: 500;
}

/* 调整单选框字体 */
.ant-radio-wrapper {
  font-size: 14px;
}

.upload-hint {
  margin-top: 8px;
  color: #8c8c8c;
  font-size: 13px;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  margin-top: 24px;

  .ant-btn {
    font-size: 14px;
    padding: 6px 16px;
  }
}

// 添加水平布局样式
.horizontal-form-row {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;

  .form-left {
    flex: 0 0 300px; // 固定宽度，与图片上传器宽度一致
  }
}

/* 修改右侧表单区域的样式 */
.form-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}

/* 水平表单项容器 */
.horizontal-form-row {
  display: flex;
  align-items: center;
  width: 100%;
}

/* 固定宽度的标签 */
.form-label {
  width: 100px;
  flex-shrink: 0;
  text-align: right;
  margin-right: 12px;
  color: rgba(0, 0, 0, 0.85);
}

/* 输入框样式 */
.horizontal-input {
  flex: 1;
  min-width: 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .horizontal-form-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-label {
    text-align: left;
    margin-bottom: 8px;
  }
}

/* 水平表单项样式 */
.horizontal-form-item {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

/* 设置标签固定宽度 */
.horizontal-form-item .ant-form-item-label {
  flex: 0 0 80px;
  text-align: left;
  margin-right: 12px;
  padding-bottom: 0;
  white-space: nowrap;
}

/* 设置输入框自适应宽度 */
.horizontal-form-item .ant-form-item-control {
  flex: 1;
  min-width: 0;
}

/* 调整输入框样式 */
.form-input {
  width: 100%;
}

/* 地图信息区域样式 */
.map-info {
  margin: 24px 0;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background-color: #fafafa;
}

.map-layout {
  display: flex;
  gap: 24px;
  min-height: 550px;
}

/* 左侧输入区域 */
.map-input-area {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.map-input-area h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.input-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-row label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.color-input {
  width: 100%;
}

.points-textarea {
  width: 100%;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
}

.help-text {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.map-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

/* 右侧地图显示区域 */
.map-display-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.map-display-area h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.map-container {
  flex: 1;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-height: 450px;
  position: relative;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .map-layout {
    flex-direction: column;
  }

  .map-input-area {
    flex: none;
    width: 100%;
  }

  .map-container {
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .map-container {
    min-height: 350px;
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .horizontal-form-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .horizontal-form-item .ant-form-item-label {
    flex: none;
    margin-bottom: 8px;
  }
}

// 响应式调整
@media (max-width: 768px) {
  .horizontal-form-row {
    flex-direction: column;

    .form-left {
      flex: none;
      width: 100%;
    }
  }
}
</style>